import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { AppConstants } from '../../shared/app.constants';
import { AppContext } from '../../shared/app.models';
import { UnknownError } from '../../shared/errors/app-error';
import { CryptoUtil } from '../../shared/utilities/crypto.utils';
import { ErrorService } from '../services/error.service';

import { CustomStorageService } from './custom-storage.service';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private readonly router = inject(Router);
  private readonly storageService = inject(CustomStorageService);
  private readonly errorService = inject(ErrorService);

  private appContext: AppContext | null = null;

  async initialize(): Promise<void> {
    const urlToken = new URLSearchParams(window.location.search).get('token');

    if (urlToken) {
      const initialized = await this.initializeFromToken(urlToken);

      if (initialized) {
        this.removeTokenFromUrl();
        return;
      }

      await this.redirectToError();
      return;
    }

    const storedContext = await this.storageService.getLocal<AppContext>(
      AppConstants.APP_CONTEXT_KEY,
    );

    if (storedContext && this.isValidContext(storedContext)) {
      this.appContext = storedContext;
      return;
    }

    await this.redirectToError();
  }

  private async initializeFromToken(token: string): Promise<boolean> {
    try {
      const decrypted = await CryptoUtil.decrypt(token, environment.secret);

      const context = JSON.parse(decrypted) as AppContext;

      if (!this.isValidContext(context)) {
        return false;
      }

      await this.storageService.setLocal(AppConstants.APP_CONTEXT_KEY, context);

      this.appContext = context;

      return true;
    } catch {
      return false;
    }
  }

  private isValidContext(context: AppContext): boolean {
    return (
      !!context &&
      typeof context.Client === 'string' &&
      context.Client.trim().length > 0 &&
      typeof context.ClinicName === 'string' &&
      context.ClinicName.trim().length > 0 &&
      Number.isInteger(context.CreatedBy) &&
      context.CreatedBy > 0 &&
      typeof context.CreatedAt === 'string' &&
      context.CreatedAt.trim().length > 0 &&
      !Number.isNaN(Date.parse(context.CreatedAt))
    );
  }

  private removeTokenFromUrl(): void {
    const url = new URL(window.location.href);

    url.searchParams.delete('token');

    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }

  private async redirectToError(): Promise<void> {
    await this.errorService.navigateToForbidden('Unable to initialize tenant context');
  }

  retrieveClientCode(): string {
    if (!this.appContext) {
      throw new UnknownError('TenantService has not been initialized');
    }

    return this.appContext.Client;
  }

  getContext(): AppContext {
    if (!this.appContext) {
      throw new UnknownError('TenantService has not been initialized');
    }

    return this.appContext;
  }

  /**
   * Check if the tenant service has a valid app context without throwing
   * @returns true if app context exists and is valid
   */
  hasValidContext(): boolean {
    return this.appContext !== null && this.isValidContext(this.appContext);
  }
}
